// Alert-Quellen der Inbox jenseits der Freigabe-Events.
// Sync-Failures gehoeren in die Alerts-Zone (Plan C3: /sync/runs/ als Quelle),
// damit eine kaputte Datenaufnahme die Morgenroutine nicht "gesund" aussehen laesst.
//
// Adversarial-Runde 17: bewertet wird je (profileId, syncType) nur der NEUESTE
// Run — alte Fehlschlaege alarmieren nicht ewig, und ein haengender running-Run
// (Fire-and-forget-Worker nach Prozessabbruch) wird als "stalled" sichtbar
// statt still als gesund durchzugehen.
import type { SyncRunSummary, SyncSafetyBlock } from '../../api/inbox'

export type { SyncRunSummary }

// Ein running-Run, der laenger als dieses Fenster laeuft, gilt als haengend.
export const STALE_RUNNING_MS = 30 * 60 * 1000

// Durabler Marker, den der Backend-Reconciler (sync/service.ts) in errors[] schreibt,
// wenn er einen verwaisten running-Run auf 'failed' setzt. Ein so markierter Run
// bedeutet: die letzte Datenaufnahme fuer (profileId, syncType) war NICHT erfolgreich —
// er MUSS Entscheidungen weiter blockieren (fail-closed), sonst verwandelt das
// Reconcile einen harten Safety-Block still in einen bloszen Alert.
export const RECONCILED_STALE_MARKER = 'reconciled_stale'

export function latestRunsPerKey(runs: SyncRunSummary[]): SyncRunSummary[] {
  const latest = new Map<string, SyncRunSummary>()
  for (const r of runs) {
    const key = `${r.profileId}:${r.syncType}`
    const prev = latest.get(key)
    if (!prev || Date.parse(r.startedAt) > Date.parse(prev.startedAt)) latest.set(key, r)
  }
  return [...latest.values()]
}

export function failedSyncRuns(runs: SyncRunSummary[]): SyncRunSummary[] {
  return latestRunsPerKey(runs).filter(
    (r) => r.status === 'failed' || fehlerListe(r.errors).length > 0 || istUnbrauchbareFehlerform(r.errors),
  )
}

export function stalledSyncRuns(runs: SyncRunSummary[], nowMs: number = Date.now()): SyncRunSummary[] {
  return latestRunsPerKey(runs).filter(
    (r) => r.status === 'running' && nowMs - Date.parse(r.startedAt) > STALE_RUNNING_MS,
  )
}

// Ist ein Run ein sauberer Erfolg? Nur dann gilt die Datenaufnahme als frisch.
function isCleanSuccess(r: SyncRunSummary): boolean {
  // Ein Wert, der KEIN Array ist, gilt nicht als sauber (fail-closed, gleiche
  // Entscheidung wie serverseitig in `modules/sync/safety-state.ts`): `?.length`
  // waere dort `undefined` und der Lauf liefe faelschlich als fehlerfrei durch.
  if (istUnbrauchbareFehlerform(r.errors)) return false
  return r.status === 'completed' && (r.errors?.length ?? 0) === 0
}

// Vom Backend-Reconciler als verwaist markierte (jetzt 'failed') Runs blockieren
// Entscheidungen weiter (fail-closed), SOLANGE fuer ihren (profileId, syncType) kein
// spaeterer, SAUBER abgeschlossener Run (completed, keine Report-Fehler) existiert.
// Wichtig: Ein bloss juengerer running-Run (noch keine 30 min = nicht "stalled") ODER
// ein completed-Run MIT Fehlern hebt die Sperre NICHT auf — sonst entstuende zwischen
// Reconcile und dem naechsten sauberen Sync ein Fenster, in dem Entscheidungen auf
// veralteten/unvollstaendigen Daten getroffen werden koennten. Erst ein sauberer
// Erfolg beweist frische Daten und loest die Sperre.

// GLB-8-Haertung (R1-Fund): `errors` ist in der Datenbank blankes `jsonb`. Waere
// dort je etwas anderes als ein Array (produktiv schreibt das niemand — die drei
// Schreibwege sind gemessen), dann wuerde `.some(...)` unten WERFEN und die
// ganze Inbox beim Rendern abstuerzen. Der Server behandelt denselben Fall
// fail-closed; hier ist die Entsprechung: kein Array ⇒ nicht als sauber
// durchgehen lassen, aber auch nicht abstuerzen.
function fehlerListe(errors: unknown): string[] {
  // Nicht nur „ist es ein Array?" (Zweitrunden-Fund R1): auch die ELEMENTE sind
  // ungeprueftes JSONB. Ein `[17]` oder `[{}]` haette beim `includes(...)` der
  // Marker-Suche weiterhin geworfen — dieselbe Absturzklasse, nur eine Ebene
  // tiefer. Nicht-Strings fallen still heraus; sie koennen keinen Marker tragen.
  return Array.isArray(errors) ? errors.filter((e): e is string => typeof e === 'string') : []
}

export function istUnbrauchbareFehlerform(errors: unknown): boolean {
  if (errors === null || errors === undefined) return false
  if (!Array.isArray(errors)) return true
  // Auch ein Array mit fremden Elementen ist unbrauchbar: es kann keinen Marker
  // tragen und darf deshalb nicht als „sauber" durchgehen.
  return errors.some((e) => typeof e !== 'string')
}

export function reconciledStaleSyncRuns(runs: SyncRunSummary[]): SyncRunSummary[] {
  const byKey = new Map<string, SyncRunSummary[]>()
  for (const r of runs) {
    const key = `${r.profileId}:${r.syncType}`
    const group = byKey.get(key)
    if (group) group.push(r)
    else byKey.set(key, [r])
  }
  const blocking: SyncRunSummary[] = []
  for (const group of byKey.values()) {
    const reconciled = group
      .filter(
        (r) => r.status === 'failed' && fehlerListe(r.errors).some((e) => e.includes(RECONCILED_STALE_MARKER)),
      )
      .sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))[0]
    if (!reconciled) continue
    const cleanSuccessNewer = group.some(
      (r) => isCleanSuccess(r) && Date.parse(r.startedAt) > Date.parse(reconciled.startedAt),
    )
    if (!cleanSuccessNewer) blocking.push(reconciled)
  }
  return blocking
}

// ── GLB-8: Beschriftung des SERVER-seitigen Safety-States ──────────────
//
// Seit GLB-8 kommt die SPERRE vom Server (`GET /sync/safety-state/`), waehrend
// die Funktionen oben die Alert-KARTEN aus dem 100er-Sample fuellen. Beides
// zusammen ist Absicht: der Server sperrt vollstaendig, das Sample zeigt
// Details. Fuer die Faelle, die das Sample NICHT enthaelt (genau das
// 100-Run-Fenster), braucht die Oberflaeche eine eigene Beschriftung — eine
// Sperre ohne genannten Grund waere die schlechteste Variante.
const SAFETY_BLOCK_TEXT: Record<SyncSafetyBlock['reason'], string> = {
  reconciled_stale: 'verwaister Lauf, danach kein sauberer Sync',
  stalled_running: 'Lauf hängt seit über 30 Minuten',
  no_clean_sync: 'noch kein sauber abgeschlossener Lauf',
}

export function safetyBlockText(block: SyncSafetyBlock): string {
  const typ = block.syncType ?? 'alle Typen'
  return `Profil ${block.profileId} · ${typ}: ${SAFETY_BLOCK_TEXT[block.reason]}`
}

// Der Backend-Status 'failed' ist ein harter Fehlschlag; ein 'completed'-Run mit
// Report-Fehlern ist nur eine Teil-Warnung (konsistent zur Sync-Seite).
export function syncRunSeverity(run: SyncRunSummary): 'failed' | 'warning' {
  return run.status === 'failed' ? 'failed' : 'warning'
}
